import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridRowSelectionModel, Toolbar, useGridApiRef } from '@mui/x-data-grid-pro';

import { MinimalTarget } from '@/generated/model';
import { SearchComponent } from '../Search';
import { SelectorTranslation } from './SelectorTypes';
import OrderController from './OrderController';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import SelectionAndOrderController from './SelectionAndOrderController';

export function SingleElementSelector({
  selectedTarget,
  setSelectedTarget,
  searchText,
  setSearchText,
  fetchData,
  getRowCount,
  columns,
  translation,
}: {
  selectedTarget: MinimalTarget | undefined;
  setSelectedTarget: (target: MinimalTarget | undefined) => void;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  fetchData: (
    page: number,
    pageSize: number,
    pagesToLoad: number,
    filter: string | undefined,
    order: string
  ) => Promise<MinimalTarget[]>;
  getRowCount: (filter: string | undefined, id: number | null) => Promise<number>;
  columns: GridColDef[];
  translation: SelectorTranslation;
}) {
  const [rows, setRows] = useState<MinimalTarget[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [rowCount, setRowCount] = useState<number>(-1);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedId, setSelectedId] = useState<number | null>(selectedTarget !== undefined ? selectedTarget.id : null);
  const apiRef = useGridApiRef();
  const pageSize = 20;

  const loadRows = useCallback(
    async (pageNo: number, pagesToLoad: number, nowHasMore: boolean) => {
      if (loading || !nowHasMore) return;
      setLoading(true);

      const newRows = await fetchData(pageNo, pageSize, pagesToLoad, searchText, order);
      if (newRows.length < pageSize) setHasMore(false); // Stop loading if no more data
      setRows((prevRows) => [...prevRows, ...newRows]);
      if (apiRef.current) {
        apiRef.current.getPropagatedRowSelectionModel({
          type: 'include',
          ids: new Set(selectedId !== null ? [selectedId] : []),
        });
      }
      setPage((prevPage) => prevPage + pagesToLoad); // Increase page count
      setLoading(false);
    },
    [fetchData, loading, order, searchText, selectedId]
  );

  const updateRowCount = useCallback(async () => {
    const response = await getRowCount(searchText, selectedId);
    setRowCount(response);
  }, [getRowCount, searchText, selectedId]);

  useEffect(() => {
    if (rows.length > 0) setSelectedTarget(rows.find((r) => r.id === selectedId));
  }, [selectedId]);

  useEffect(() => {
    async function search() {
      await updateRowCount();
      setRows([]);
      setHasMore(true);
      setPage(0);
      await loadRows(0, 1, true);
    }
    void search();
  }, [searchText, order]);

  async function loadMoreRows() {
    await loadRows(page, 1, hasMore);
  }

  function handleSelectionChange(newSelection: GridRowSelectionModel) {
    const id = newSelection.ids.size ? newSelection.ids.values().next().value : null;
    setSelectedId(typeof id === 'number' ? id : null);
  }

  // FIXME sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}
  function getColumnHeaders() {
    return {
      toolbar: () => (
        <Toolbar style={{ minHeight: '63px' }}>
          <div className="selector-header-container">
            <SelectionAndOrderController
              selectionState="single-select"
              totalRows={rowCount}
              orderController={<OrderController order={order} setOrder={setOrder} disabled={rowCount <= 0} />}
            />
          </div>
        </Toolbar>
      ),
    };
  }

  function getStyle() {
    return {
      '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
        display: 'none',
      },
      '& .MuiDataGrid-virtualScroller': {
        marginTop: '0 !important',
      },
    };
  }

  return (
    <>
      {!isPhoneOrHighScreen() && (
        <Typography variant="h6" data-qa-element-id="single_target_selector_title">
          {translation.header}
        </Typography>
      )}
      <SearchComponent searchText={searchText} setSearchText={setSearchText} placeholder={translation.placeHolder} />
      <DataGridPro
        showToolbar
        rows={rows}
        columns={columns}
        pageSizeOptions={[30]}
        paginationMode="server"
        onRowsScrollEnd={loadMoreRows}
        loading={loading}
        rowCount={rowCount}
        onRowSelectionModelChange={handleSelectionChange}
        apiRef={apiRef}
        hideFooter
        sx={getStyle()}
        columnHeaderHeight={0}
        localeText={{ noRowsLabel: translation.noRows }}
        slots={getColumnHeaders()}
      />
    </>
  );
}
